import { IScope, IDirective, IDirectiveFactory, IQService } from "angular";
import { IMoment, ILinkedObjectivesMomentsIdMap, IMomentChange, IMomentChanges } from "./models";
import { IAssigneeIdMap } from "@gtmhub/assignees";
import { CommentService, CommentTargetType } from "@gtmhub/comments";
import { TimelineService } from "./timeline.service";
import { localize } from "@gtmhub/localization";
import { GoalService } from "../goals/goal.service";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IGoal } from "../goals/models";
import { ICollection } from "../models";
import { INgRedux } from "@gtmhub/state-management";
import { AssigneeActions } from "@gtmhub/assignees/redux/assignee-actions";
import { ICustomField, CustomFieldActions, CustomFieldNameMap, ICustomFieldsStoreState } from "@gtmhub/customFields";
import { IAssigneesStoreState } from "@gtmhub/assignees";
import { StateService } from "@uirouter/angularjs";

interface IAttachmentTypeMap {
  0: string;
  1: string;
  2: string;
}

export interface ITimelineScope extends IScope {
  kind: string;
  targetIds: string;
  targetId: string;
  target: string;
  indicators: {
    loadingMoments: boolean;
  };
  allMomentsLoaded: boolean;
  linkedObjectivesMomentsIdMap: any;
  moments: IMoment[];
  assigneesIdMap: IAssigneeIdMap;
  attainmentTypeMap: IAttachmentTypeMap;
  customFieldNameMap: CustomFieldNameMap;
  isCustomField(change: string): boolean;
  isArray(change: string | string[]): boolean;
  getCustomFieldLabel(changes, index: number): void;
  getValues(type: string, moment: IMoment, change: string): string | string[];
  workflowChange(change: number): void;
  okrStatusChange(change: number): void;
  getCfInlineValue(customFieldValue: string | string[]): string | string[];
  reviewersChange(change): string;
  goToGoal(id: string): void;
  loadMore(skip: number): void;
  getCustomFieldLabelByName(name: string): string;
  loadLinkedObjectivesDetails(momentId: string, momentChanges: { oldValue: any[]; newValue: any[] }): void;
}

interface ICommentsCountScope extends IScope {
  count: number;
  targetId: string;
  targetIds: string;
  targetType: CommentTargetType;
}

export class Commentscount implements IDirective {
  public scope = {
    targetId: "@",
    targetIds: "@",
    targetType: "@",
  };
  public restrict = "E";
  public transclude = true;
  public template = "{{count}}";

  constructor(private commentService: CommentService) {}

  public link(scope: ICommentsCountScope): void {
    this.loadMoments(scope);
  }

  private loadMoments = (scope: ICommentsCountScope) => {
    this.commentService.getComments({ targetType: scope.targetType, targetIds: scope.targetIds }).then((c) => {
      scope.count = c.totalCount;
    });
  };

  public static factory(): IDirectiveFactory {
    const directive = (commentService: CommentService) => new Commentscount(commentService);
    directive.$inject = ["CommentService"];
    return directive;
  }
}

const bindStateToScope = (state: IAssigneesStoreState & ICustomFieldsStoreState): Record<string, unknown> => {
  const assigneesIdMap = state.assignees.map;
  const customFieldNameMap = Object.values(state.customFields.map).reduce((customFieldNameMap, typeCfArr: ICustomField[]) => {
    typeCfArr.forEach((customField) => {
      customFieldNameMap[customField.name] = customField;
    });

    return customFieldNameMap;
  }, {});

  return {
    assigneesIdMap,
    customFieldNameMap,
  };
};
export class Timeline implements IDirective {
  public scope = {
    content: "@",
    target: "@",
    kind: "@",
    targetId: "=",
    targetIds: "=",
  };
  public restrict = "E";
  public transclude = true;
  public templateUrl = require("./views/timeline.jade");

  constructor(
    private $state: StateService,
    private timelineService: TimelineService,
    private customFieldActions: CustomFieldActions,
    private $q: IQService,
    private goalService: GoalService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions
  ) {}

  public link(scope: ITimelineScope): void {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(scope);
    scope.$on("$destroy", unsubscribe);

    this.$q.all([this.$ngRedux.dispatch(this.assigneeActions.getAssignees()), this.$ngRedux.dispatch(this.customFieldActions.getCustomFields())]);

    scope.indicators = {
      loadingMoments: false,
    };
    scope.linkedObjectivesMomentsIdMap = {};

    scope.$on("timelineUpdated", () => {
      this.loadMoments(scope, 0);
    });

    scope.isCustomField = (change: string): boolean => {
      return change.split(".")[0] === "customFields";
    };

    scope.isArray = (change: string | string[]): boolean => {
      return Array.isArray(change);
    };

    scope.getCustomFieldLabel = (change: string): string => {
      const changeName = change.split(".")[1];
      return scope.customFieldNameMap && changeName && scope.customFieldNameMap[changeName] ? scope.customFieldNameMap[changeName].label : "";
    };

    scope.getCustomFieldLabelByName = (name: string): string => {
      return scope.customFieldNameMap[name].label;
    };

    scope.getCfInlineValue = (customFieldValue: string | string[]): string | string[] => {
      return Array.isArray(customFieldValue) ? customFieldValue.join(", ") : customFieldValue;
    };

    scope.getValues = (type: "old" | "new", moment: IMoment, change: string): string | string[] => {
      if (moment && change) {
        if (Array.isArray(moment.content.changes[change][type + "Value"])) {
          return this.extractArrayCustomValues(type, moment, change);
        } else {
          return moment.content.changes[change][type + "Value"];
        }
      } else {
        return "";
      }
    };

    const workflowStatus: Record<string, string> = {
      draft: localize("draft"),
      inReview: localize("inReview"),
      needsReviewers: localize("needsReviewers"),
    };

    scope.workflowChange = (change: number): string => {
      switch (change) {
        case 0:
          return workflowStatus.draft;
        case 1:
          return workflowStatus.needsReviewers;
        case 2:
          return workflowStatus.inReview;
      }
    };

    const okrStatus: Record<string, string> = {
      active: localize("active"),
      achieved: localize("achieved"),
      abandoned: localize("abandoned"),
    };

    scope.okrStatusChange = (change: number): string => {
      switch (change) {
        case 0:
          return okrStatus.active;
        case 1:
          return okrStatus.achieved;
        case 2:
          return okrStatus.abandoned;
      }
    };

    const reviewerActions: Record<string, string> = {
      addedReviewers: localize("addedReviewers"),
      removedReviewers: localize("removedReviewers"),
      changedReviewers: localize("changedReviewers"),
      reviewerApproved: localize("reviewerApproved"),
      reviewerDeclined: localize("reviewerDeclined"),
    };

    const convertIdsToNames = (assigneeIds: string[]): string => {
      assigneeIds = assigneeIds.reduce((names, id) => {
        names.push(scope.assigneesIdMap[id].name);

        return names;
      }, []);

      return assigneeIds.join(", ");
    };

    const getIds = (x) =>
      x.reduce((names, name) => {
        names.push(name.UserId);
        return names;
      }, []);

    scope.reviewersChange = (change): string => {
      const reviewersNewIds = getIds(change.newValue);
      const reviewersOldIds = getIds(change.oldValue);

      // reviewersIds holds the ids from the old and new value changes
      let reviewersIds = [...reviewersOldIds, ...reviewersNewIds].reduce((ids, curr) => ((ids[curr] = ++ids[curr] || 1), ids), {});

      // get only the ids which appears once to get the user who have approved or declined the objective
      reviewersIds = Object.keys(reviewersIds).filter((id) => {
        return reviewersIds[id] === 1;
      });

      if (change.newValue.length > change.oldValue.length) {
        return reviewerActions.addedReviewers + " " + convertIdsToNames(reviewersIds);
      }
      if (change.oldValue.length > change.newValue.length) {
        return reviewerActions.removedReviewers + " " + convertIdsToNames(reviewersIds);
      }

      if (change.oldValue.length === change.newValue.length) {
        // the array holds approved/declined message so that we can avoid checking wheter the response is 0 1 or 2
        // and avoid 3 if-s
        const approveOrDecline = ["", reviewerActions.reviewerApproved, reviewerActions.reviewerDeclined];

        for (let i = 0; i < change.oldValue.length; i++) {
          if (change.oldValue[i].UserId === change.newValue[i].UserId) {
            if (change.oldValue[i].Response !== change.newValue[i].Response && change.newValue[i].Response !== 0) {
              return scope.assigneesIdMap[change.newValue[i].UserId].name + " " + approveOrDecline[change.newValue[i].Response];
            }
          } else {
            return reviewerActions.changedReviewers + " " + convertIdsToNames(reviewersNewIds);
          }
        }
      }
    };

    scope.attainmentTypeMap = {
      0: localize("key_results"),
      1: localize("child_objectives"),
      2: localize("key_results_and_child_objectives"),
    };

    scope.goToGoal = (id: string) => {
      this.$state.go("^.^").then(() => {
        this.$state.go(".", { id: id });
      });
    };

    scope.loadMore = () => {
      this.loadMoments(scope, scope.moments.length);
    };

    this.loadMoments(scope, 0);

    scope.loadLinkedObjectivesDetails = (momentId: string, momentChanges: { oldValue: any[]; newValue: any[] }): void => {
      // checking if the goals' data is not already fetched about this particular moment
      if (!scope.linkedObjectivesMomentsIdMap[momentId]) {
        scope.indicators[momentId] = true;

        const linkedObjectiveIds = this.processMomentInIdMap(scope, momentId, momentChanges);

        // a request so I can enrich the new and old goals ids map with their names and sessions
        this.goalService.getGoals({ fields: "name,sessionId", goalsIds: linkedObjectiveIds }).then(
          (linkedObjectives: IGoal[]) => {
            scope.indicators[momentId] = false;
            // this makes the data visible only if the request is successful
            scope.linkedObjectivesMomentsIdMap[momentId].collapsed = true;

            this.processObjectivesNamesInMomentIdMap(scope, momentId, linkedObjectives);
          },
          (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: scope.indicators[momentId], inline: true })
        );
      } else {
        // if the data is already fetched, I only change the collapsed property so the view knows wether to show the additional data or not
        scope.linkedObjectivesMomentsIdMap[momentId].collapsed = !scope.linkedObjectivesMomentsIdMap[momentId].collapsed;
      }
    };
  }

  private extractArrayCustomValues = (type: "old" | "new", moment: IMoment, change: string): string[] => {
    const newValues: string[] = moment.content.changes[change]["newValue"];
    const oldValues: string[] = moment.content.changes[change]["oldValue"];

    if (type === "old") {
      if (oldValues == null) {
        return [];
      } else {
        return newValues == null ? oldValues : oldValues.filter((oldVal) => !newValues.includes(oldVal));
      }
    } else {
      if (newValues == null) {
        return [];
      } else {
        return oldValues == null ? newValues : newValues.filter((newVal) => !oldValues.includes(newVal));
      }
    }
  };

  private processMomentInIdMap = (scope: ITimelineScope, momentId: string, momentChanges: IMomentChanges): string => {
    // these are all objectives' ids, which are a part of a moment where a change of the linked supported/supporting objective has happened
    const linkedObjectiveIds = [];

    // creating this moment in the map
    scope.linkedObjectivesMomentsIdMap[momentId] = {
      collapsed: false,
      oldObjectivesValueIdMap: {},
      newObjectivesValueIdMap: {},
    };

    const currentMoment: ILinkedObjectivesMomentsIdMap = scope.linkedObjectivesMomentsIdMap[momentId];
    const newValues: IMomentChange[] = momentChanges.newValue || [];
    const oldValues: IMomentChange[] = momentChanges.oldValue || [];

    // iterating over the old and new values, so I can extract all the goals' ids so I can request additional data
    if (momentChanges.oldValue) {
      momentChanges.oldValue = oldValues.filter((oldValue) => !newValues.includes(oldValue));

      momentChanges.oldValue.forEach((value) => {
        linkedObjectiveIds.push(value.id);
        currentMoment.oldObjectivesValueIdMap[value.id] = {};
      });
    }

    if (momentChanges.newValue) {
      momentChanges.newValue = newValues.filter((newValue) => !oldValues.includes(newValue));
      momentChanges.newValue.forEach((value) => {
        linkedObjectiveIds.push(value.id);
        currentMoment.newObjectivesValueIdMap[value.id] = {};
      });
    }

    return linkedObjectiveIds.join(",");
  };

  private processObjectivesNamesInMomentIdMap = (scope: ITimelineScope, momentId: string, linkedObjectives: IGoal[]): void => {
    const currentMoment = scope.linkedObjectivesMomentsIdMap[momentId];

    linkedObjectives.forEach((objective) => {
      const newLinkedObjectiveInIdMap = currentMoment.newObjectivesValueIdMap[objective.id];
      const oldLinkedObjectiveInIdMap = currentMoment.oldObjectivesValueIdMap[objective.id];

      if (oldLinkedObjectiveInIdMap) {
        oldLinkedObjectiveInIdMap.name = objective.name;
        oldLinkedObjectiveInIdMap.sessionId = objective.sessionId;
      }

      if (newLinkedObjectiveInIdMap) {
        newLinkedObjectiveInIdMap.name = objective.name;
        newLinkedObjectiveInIdMap.sessionId = objective.sessionId;
      }
    });
  };

  private momentStrings = {
    objectiveSupporting: "objectiveSupporting",
    targets: {
      metric: "metric",
      goal: "goal",
      objectiveLink: "objective_link",
      task: "task",
    },
    kinds: {
      created: "created",
      modified: "modified",
      deleted: "deleted",
      goalConfidenceChange: "goalConfidenceChange",
      comment: "comment",
    },
    types: {
      confidenceChange: "confidenceChange",
      taskCreated: "taskCreated",
      taskModified: "taskModified",
      goalModified: "goalModified",
      goalCreated: "goalCreated",
      metricAdded: "metricAdded",
      metricDeleted: "metricDeleted",
      metricModified: "metricModified",
      progressUpdated: "progressUpdated",
      designScore: "designScore",
      progressChanged: "progressChanged",
    },
  };

  private convertProgress = (moment: IMoment): string => {
    const allowedChanges = {
      metricsCount: localize("number_of_key_results_cap"),
      attainment: localize("objective_progress"),
      aggregatedAttainment: localize("aggregated_progress"),
      fullSubTreeCount: localize("number_of_child_objectives"),
    };

    if (moment.content && moment.content.changes) {
      moment.content.changes = Object.keys(moment.content.changes)
        .filter((change) => allowedChanges[change])
        .reduce((obj, key) => {
          if (key !== "metricsCount" && key !== "fullSubTreeCount") {
            moment.content.changes[key].oldValue = Math.round(moment.content.changes[key].oldValue * 100);
            moment.content.changes[key].newValue = Math.round(moment.content.changes[key].newValue * 100);
          }

          obj[allowedChanges[key]] = moment.content.changes[key];
          return obj;
        }, {});
    }

    return this.momentStrings.types.progressChanged;
  };

  private momentTypes = {
    goal: {
      created: () => this.momentStrings.types.goalCreated,
      modified: (moment) => this.goalModifiedSubcategories(moment),
      goalConfidenceChange: () => this.momentStrings.types.confidenceChange,
      ["progress changed"]: (moment) => this.convertProgress(moment),
    },
    metric: {
      created: () => this.momentStrings.types.metricAdded,
      modified: (moment) => this.metricModifiedSubcategories(moment),
      deleted: () => this.momentStrings.types.metricDeleted,
    },
    objective_link: {
      created: () => this.momentStrings.objectiveSupporting, // yes, created and deleted are the same moment type, at least for now
      deleted: () => this.momentStrings.objectiveSupporting,
    },
    comment: () => this.momentStrings.kinds.comment,
  };

  private metricModifiedSubcategories = (moment: IMoment): string => {
    moment.type = this.momentStrings.types.metricModified;

    if (moment.content.changes && moment.content.changes.name) {
      moment.displayName = moment.content.changes.name.oldValue;
    } else if (moment.content.metadata) {
      moment.displayName = moment.content.metadata.name;
    }

    if (moment.content.changes && typeof moment.content.changes.actual) {
      moment.type = this.momentStrings.types.progressUpdated;
    }

    return moment.type;
  };

  private goalModifiedSubcategories = (moment: IMoment): string => {
    moment.type = this.momentStrings.types.goalModified;

    // tasks created
    if (moment.content.changes && typeof moment.content.changes.task !== "undefined" && moment.content.changes.task.oldValue == null) {
      moment.type = this.momentStrings.types.taskCreated;
      moment.content.changes.task.newValue = JSON.parse(moment.content.changes.task.newValue);
    }

    if (moment.content.changes && moment.content.changes["designScore.policies"]) {
      moment.type = this.momentStrings.types.designScore;
    }

    if (moment.content.changes && moment.content.changes.description !== undefined) {
      let description = moment.content.changes.description.newValue;
      const mentions = this.extractMention(description);
      if (mentions.length > 0) {
        mentions.forEach((mention) => {
          const parts = mention.split(":");
          const fullName = parts[0].substring(2, parts[0].length);
          description = description.replace(mention, fullName);
        });
        moment.content.changes.description.newValue = description;
      }
    }

    if (moment.content.changes && moment.content.changes.tags !== undefined) {
      const oldValues = moment.content.changes.tags.oldValue;
      const newValues = moment.content.changes.tags.newValue;

      const oldTags = oldValues !== null ? oldValues.flatMap((curr) => curr.name) : [];
      const newTags = newValues !== null ? newValues.flatMap((curr) => curr.name) : [];
      moment.content.changes.tags.added = newTags.filter((newTag) => !oldTags.includes(newTag));
      moment.content.changes.tags.removed = oldTags.filter((oldTag) => !newTags.includes(oldTag));
    }

    return moment.type;
  };

  private extractMention = (description: string): string[] => {
    const re = /@\[(.*?)\]/g;

    const result = description.match(re);
    if (!result || result.length === 0) {
      return [];
    }

    return result;
  };

  private removeUnusedMomentChangeKeys = (moment: IMoment): void => {
    const unusedMomentChangeKeys = ["metricGoalId", "lastSnapshotAt", "latestSnapshotAt", "confidence.userId", "confidence.date"];

    for (const key in moment.content.changes || {}) {
      if ((key === "attainment" && moment.target === "metric") || key.indexOf("mentions") >= 0 || unusedMomentChangeKeys.includes(key)) {
        delete moment.content.changes[key];
      }
    }
  };

  private getMomentType = (moment: IMoment): string => {
    return this.momentTypes[moment.target] && this.momentTypes[moment.target][moment.kind] ? this.momentTypes[moment.target][moment.kind].call(null, moment) : undefined;
  };

  private loadMoments = (scope: ITimelineScope, skip: number) => {
    scope.indicators.loadingMoments = true;

    this.timelineService.getGoalHistory(scope.targetId, scope.target, { skip: skip, take: 10 }).then(
      (moments: ICollection<IMoment>) => {
        scope.indicators.loadingMoments = false;
        scope.moments = scope.moments || [];
        scope.allMomentsLoaded = moments.items.length < 10;

        if (moments.items) {
          for (let i = 0; i < moments.items.length; i++) {
            const moment = moments.items[i];
            moment.content = typeof moment.content === "string" ? JSON.parse(moment.content) : moment.content;

            if (moment.content.type === "supporting" || (moment.target === "metric" && moment.kind === "progress changed")) {
              continue;
            }

            this.removeUnusedMomentChangeKeys(moment);

            moment.type = this.getMomentType(moment);
            scope.moments.push(moment);
          }
        }
      },
      (err) => {
        this.uiErrorHandlingService.handleIndicator(err, { indicator: "loadingMoments", inline: true });
      }
    );
  };

  public static factory(): IDirectiveFactory {
    const directive = (
      $state: StateService,

      timelineService: TimelineService,
      customFieldActions: CustomFieldActions,
      $q: IQService,
      goalService: GoalService,
      uiErrorHandlingService: UIErrorHandlingService,
      $ngRedux: INgRedux,
      assigneeActions: AssigneeActions
    ) => new Timeline($state, timelineService, customFieldActions, $q, goalService, uiErrorHandlingService, $ngRedux, assigneeActions);

    directive.$inject = ["$state", "TimelineService", "CustomFieldActions", "$q", "GoalService", "UIErrorHandlingService", "$ngRedux", "AssigneeActions"];

    return directive;
  }
}
